import type { OutboxItemState } from '@Domain/Ports/Outbox/OutboxPort';
import openDeviceDatabase, { OUTBOX_STORE_NAME } from '@Infra/Device/openDeviceDatabase';
import waitForIndexedDbRequest from '@Infra/Device/waitForIndexedDbRequest';
import waitForIndexedDbTransaction from '@Infra/Device/waitForIndexedDbTransaction';

/** Injected time source: lets the 24-hour purge window be asserted exactly in tests instead of racing the wall clock. */
type Clock = {
	now(): number;
};

/**
 * The row shape actually written to the `outbox` store.
 *
 * `segmentCount`, `byteSize` and `expiresAt`, which `OutboxItem` exposes to its callers, are
 * deliberately absent here: the first two are derived from the buffer's segment list, whose
 * `chunks` store already is the source of truth, and `expiresAt` is derived from `retentionMs` and
 * `createdAt`. Persisting any of them would create a second writer for the same fact.
 */
type OutboxRecord = {
	readonly id: string;
	readonly kind: string;
	readonly state: OutboxItemState;
	readonly mimeType: string;
	readonly createdAt: number;
	readonly updatedAt: number;
	readonly durationMs: number | null;
	readonly pendingSince: number | null;
	readonly confirmedAt: number | null;
	readonly attempts: number;
	readonly nextAttemptAt: number | null;
	readonly progress: number | null;
	/** French, displayable as-is. */
	readonly lastErrorMessage: string | null;
	readonly ownerUserId: number;
	/** Technical identifiers only — never a name, never a verbatim. Erased on confirmation. */
	readonly context: Readonly<Record<string, string | number>>;
	/** Retention granted to this item, in milliseconds; `null` means "never expires". */
	readonly retentionMs: number | null;
	/** Timestamp of erasure by expiry; `null` outside the `'expired'` state. */
	readonly expiredAt: number | null;
	/** Incremented only on an integrity check failure; never reset back to zero. */
	readonly integrityFailureCount: number;
};

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const OPERATION_ERROR_MESSAGE = "Échec de l'opération sur la file d'attente.";
const TRANSACTION_ERROR_MESSAGE = "Échec de la transaction sur la file d'attente.";
const TRANSACTION_ABORT_MESSAGE = "Transaction sur la file d'attente interrompue.";

/**
 * Normalises a record read from storage against the three fields this store added after some
 * records already existed on devices: a row written before that point carries none of them, so a
 * plain read would surface `undefined`, a value this store's declared types do not describe. This
 * normalisation is the migration — IndexedDB has no per-record schema to bump.
 */
const normalizeRecord = (record: OutboxRecord): OutboxRecord => {
	return {
		...record,
		retentionMs: record.retentionMs ?? null,
		integrityFailureCount: record.integrityFailureCount ?? 0,
		expiredAt: record.expiredAt ?? null,
	};
};

/**
 * Persists the synchronisation queue's metadata in the `outbox` store, and nothing else: ordering,
 * backoff scheduling and the integrity barrier are the runner's concern, not this store's.
 *
 * The database is opened lazily, on the first call that genuinely needs it, and the connection is
 * memoised so building this store never deposits a database on the device by itself.
 *
 * Every read is by exact key, or by the `byState` index on an exact value: `IDBKeyRange` is
 * deliberately never referenced here. The index already exposes the one granularity this store
 * needs, and the global itself is absent from this project's test harness.
 */
class IndexedDbOutboxStore {
	private readonly indexedDbFactory: IDBFactory | undefined;
	private readonly clock: Clock;
	private databasePromise: Promise<IDBDatabase> | null;

	constructor(
		indexedDbFactory: IDBFactory | undefined = globalThis.indexedDB,
		clock: Clock = { now: () => Date.now() },
	) {
		this.indexedDbFactory = indexedDbFactory;
		this.clock = clock;
		this.databasePromise = null;
	}

	isSupported(): boolean {
		return Boolean(this.indexedDbFactory);
	}

	async save(record: OutboxRecord): Promise<void> {
		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readwrite');
		transaction.objectStore(OUTBOX_STORE_NAME).put(record);
		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	async findById(id: string): Promise<OutboxRecord | null> {
		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readonly');
		const request = transaction.objectStore(OUTBOX_STORE_NAME).get(id);
		const record: OutboxRecord | undefined = await waitForIndexedDbRequest(
			request,
			OPERATION_ERROR_MESSAGE,
		);

		return record === undefined ? null : normalizeRecord(record);
	}

	async listAll(): Promise<readonly OutboxRecord[]> {
		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readonly');
		const request = transaction.objectStore(OUTBOX_STORE_NAME).getAll();
		const records: readonly OutboxRecord[] = await waitForIndexedDbRequest(
			request,
			OPERATION_ERROR_MESSAGE,
		);

		return records.map(normalizeRecord);
	}

	async listByState(state: OutboxItemState): Promise<readonly OutboxRecord[]> {
		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readonly');
		const request = transaction.objectStore(OUTBOX_STORE_NAME).index('byState').getAll(state);
		const records: readonly OutboxRecord[] = await waitForIndexedDbRequest(
			request,
			OPERATION_ERROR_MESSAGE,
		);

		return records.map(normalizeRecord);
	}

	async deleteById(id: string): Promise<void> {
		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readwrite');
		transaction.objectStore(OUTBOX_STORE_NAME).delete(id);
		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	/**
	 * Deletes the entire record of every confirmed item older than 24 hours — never a field. By
	 * confirmation time the context is already cleared and the bytes already deleted, so what
	 * remains is only a display trace, and that is exactly what this removes.
	 *
	 * The caller decides when this runs: on hydration and after every confirmation, never on a
	 * timer owned by this store.
	 */
	async purgeConfirmedOlderThan24Hours(): Promise<void> {
		const confirmedRecords = await this.listByState('confirmed');
		const now = this.clock.now();
		const staleRecordIds = confirmedRecords
			.filter(
				(record) => record.confirmedAt !== null && now - record.confirmedAt > TWENTY_FOUR_HOURS_MS,
			)
			.map((record) => record.id);

		if (staleRecordIds.length === 0) {
			return;
		}

		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readwrite');
		const objectStore = transaction.objectStore(OUTBOX_STORE_NAME);

		for (const staleRecordId of staleRecordIds) {
			objectStore.delete(staleRecordId);
		}

		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	/**
	 * Deletes the entire record of every `'expired'` item older than `ms` — never a field, and
	 * across every owner, never only the current one. `discard()` is owner-scoped, so on a shared
	 * device the trace of a colleague who never reconnects would otherwise be erasable by nobody.
	 * By this age the bytes are already gone (the caller has already reconciled every `'expired'`
	 * record through `LocalBufferPort.deleteItem`), so what remains is only a display trace.
	 *
	 * The caller decides when this runs, and must run it only after that reconciliation: this store
	 * never touches `LocalBufferPort` and never deletes bytes itself.
	 */
	async purgeExpiredOlderThan(ms: number): Promise<void> {
		const expiredRecords = await this.listByState('expired');
		const now = this.clock.now();
		const staleRecordIds = expiredRecords
			.filter((record) => record.expiredAt !== null && now - record.expiredAt > ms)
			.map((record) => record.id);

		if (staleRecordIds.length === 0) {
			return;
		}

		const database = await this.openDatabase();
		const transaction = database.transaction(OUTBOX_STORE_NAME, 'readwrite');
		const objectStore = transaction.objectStore(OUTBOX_STORE_NAME);

		for (const staleRecordId of staleRecordIds) {
			objectStore.delete(staleRecordId);
		}

		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	private openDatabase(): Promise<IDBDatabase> {
		if (this.indexedDbFactory === undefined) {
			return Promise.reject(new Error("IndexedDB n'est pas disponible sur cet appareil."));
		}

		if (this.databasePromise === null) {
			this.databasePromise = openDeviceDatabase(this.indexedDbFactory);
		}

		return this.databasePromise;
	}
}

export default IndexedDbOutboxStore;
export type { OutboxRecord, Clock };
